import { Button, Flex, Stack, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { BatchDocumentTransactions } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import { DocumentTransactionTable } from './DocumentTransactionTable';

interface Props {
	documents: BatchDocumentTransactions[];
	isLoading: boolean;
	onBack: () => void;
	onMerge?: (transactionId: string) => void;
	onDiscard?: (transactionId: string) => void;
	mergedTransactionIds?: Set<string>;
}

export const BatchTransactionList = ({
	documents,
	isLoading,
	onBack,
	onMerge,
	onDiscard,
	mergedTransactionIds,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	if (isLoading) {
		return (
			<div style={{ position: 'relative', minHeight: 100 }}>
				<InlineLoader withoutBackground />
			</div>
		);
	}

	const totalTransactions = documents.reduce((sum, doc) => sum + doc.transactions.length, 0);

	if (totalTransactions === 0) {
		return (
			<Stack gap="md">
				<Button
					variant="subtle"
					size="compact-sm"
					leftSection={<Icon variant="arrow-left" size="md" />}
					onClick={onBack}
					styles={{ root: { alignSelf: 'flex-start' } }}
				>
					{tCapManage('transactions.add_from_document.back_to_batches')}
				</Button>
				<Flex direction="column" align="center" justify="center" py="xl" gap="xs">
					<Icon variant="document-v3" size="xl" />
					<Text size="sm" color="#4F616C">
						{tCapManage('transactions.add_from_document.no_transactions_in_batch')}
					</Text>
				</Flex>
			</Stack>
		);
	}

	return (
		<Stack gap="md">
			<Flex align="center" justify="space-between">
				<Button
					variant="subtle"
					size="compact-sm"
					leftSection={<Icon variant="arrow-left" size="md" />}
					onClick={onBack}
				>
					{tCapManage('transactions.add_from_document.back_to_batches')}
				</Button>
				<Text size="sm" color="#4F616C">
					{totalTransactions === 1
						? tCapManage(
								'transactions.add_from_document.transactions_across_documents',
								{ txCount: totalTransactions, docCount: documents.length }
							)
						: tCapManage(
								'transactions.add_from_document.transactions_across_documents_plural',
								{ txCount: totalTransactions, docCount: documents.length }
							)}
				</Text>
			</Flex>

			{documents.map((doc) => (
				<DocumentTransactionTable
					key={doc.documentId}
					doc={doc}
					onMerge={onMerge}
					onDiscard={onDiscard}
					mergedTransactionIds={mergedTransactionIds}
				/>
			))}
		</Stack>
	);
};
